/**
 * 엑셀 조립·해체는 **브라우저에서** 한다 (harness/UI-SPEC.md §6 — Workers CPU 10ms).
 *
 * SheetJS 는 npm 의 `xlsx` 패키지가 오래 방치돼 있어(취약점 포함) 여기서는
 * 실행 시점에 공식 CDN 에서 불러온다. 빌드·CI 가 외부 CDN 에 묶이지 않고,
 * 엑셀을 쓰지 않는 화면에서는 아예 내려받지 않는다.
 *
 * 못 불러오면 **조용히 실패하지 않는다** — 사용자에게 이유를 말한다(SOUL S1·S5).
 */

const SHEETJS_URL = "https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs";

interface SheetJs {
  utils: {
    json_to_sheet(rows: Record<string, unknown>[], opts?: { header?: string[] }): unknown;
    book_new(): unknown;
    book_append_sheet(book: unknown, sheet: unknown, name: string): void;
    sheet_to_json<T>(sheet: unknown, opts?: { defval?: unknown; raw?: boolean }): T[];
  };
  writeFile(book: unknown, filename: string): void;
  read(data: ArrayBuffer, opts?: { type?: string; cellDates?: boolean }): {
    SheetNames: string[];
    Sheets: Record<string, unknown>;
  };
}

let cached: Promise<SheetJs> | null = null;

export function loadSheetJs(): Promise<SheetJs> {
  if (!cached) {
    cached = import(/* @vite-ignore */ SHEETJS_URL).then((m) => m as unknown as SheetJs);
    cached.catch(() => {
      cached = null; // 다음 시도에서 다시 받도록
    });
  }
  return cached;
}

/** 보고 있는 그대로를 파일로 (UI-SPEC §5 — 오래 걸리면 진행 표시는 호출부가 한다) */
export async function downloadRowsAsXlsx(
  rows: Record<string, unknown>[],
  filename: string,
  sheetName = "케이스",
): Promise<void> {
  const XLSX = await loadSheetJs();
  const header = rows.length > 0 ? Object.keys(rows[0]!) : [];
  const sheet = XLSX.utils.json_to_sheet(rows, header.length > 0 ? { header } : undefined);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, sheetName);
  XLSX.writeFile(book, filename);
}

/** 첫 시트를 행 배열로. 값은 문자열 그대로 받는다 — 정규화는 호출부가 한다 */
export async function readFirstSheet(file: File): Promise<Record<string, unknown>[]> {
  const XLSX = await loadSheetJs();
  const book = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true });
  const first = book.SheetNames[0];
  if (!first) return [];
  return XLSX.utils.sheet_to_json<Record<string, unknown>>(book.Sheets[first], { defval: null });
}
